import React, { FC, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import { Container } from "@material-ui/core";
import TextFieldComponent from "../../components/TextFieldComponent";
import ButtonComponent from "../../components/ButtonComponent";
import Title from "../../components/Title";
import Avatar from "@material-ui/core/Avatar";
import DatePickerComponent from "../../components/DatePickerComponent";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { modules } from "../../constants/serverErrors-constants";

import {
  signup,
  clearAuthLoading,
  profileInfo,
} from "../../flux/actions/auth-actions";

import moment from "moment";
import { utilities } from "../../helpers/utilities";
import UserModel from "../../models/user-model";
import * as Validations from "../../helpers/validations";

//styles
import { useStyles } from "./styles";
import { date } from "../../helpers/validations";
import {
  authReducer,
  AuthReducerState,
} from "../../flux/reducers/auth-reducer";
import ScreenLoader from "../../components/ScreenLoader";

const MyProfile: FC = () => {
  //initialize styles
  const classes = useStyles();

  //Redux
  const dispatcher = useDispatch();
  const authReducer: AuthReducerState = useSelector(
    (state: any) => state.authReducer
  );
  const user: UserModel = useSelector((state: any) => state.authReducer.user);

  const { full_name, birth_date, email: userEmail, phone: phoneNumber } = user;

  useEffect(() => {
    if (user && !user.id) {
      window.location.href = "/pageNotFound";
    }
  }, [user]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (
      authReducer.errorStatus === 422 &&
      authReducer.errorMessage?.includes(modules.EDIT_USER_INFO_ERROR)
    ) {
      setErrors({ ...errors, email: "Este email ya está en uso." });
      setEditableFields(false);
    }
  }, [authReducer.errorStatus]);

  useEffect(() => {
    dispatcher(clearAuthLoading());
  }, [dispatcher]);

  React.useEffect(() => {
    document
      .querySelector("input[type='number']")
      ?.addEventListener("keypress", (evt: any) => {
        if (evt.which === 8) {
          return;
        }
        if (evt.which < 48 || evt.which > 57) {
          evt.preventDefault();
        }
      });
  }, []);

  //Hooks
  const [name, setName] = useState(full_name);
  const [birthDate, setBirthDate] = useState<Date | null>(birth_date);
  const [email, setEmail] = useState(userEmail);
  const [phone, setPhone] = useState(phoneNumber);
  const [editableFields, setEditableFields] = useState(true);

  const [errors, setErrors] = useState({
    fullName: "",
    birthDate: "",
    email: "",
    phone: "",
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    //Validations
    const nameErrors = Validations.validate([Validations.required], name);
    const emailErrors = Validations.validate(
      [Validations.required, Validations.emailText],
      email
    );

    let dateError = Validations.dateRequired(date);
    const range = moment().diff(birthDate, "years");
    dateError =
      dateError === "" && range <= 18
        ? "Debes ser mayor de edad para registrarte"
        : dateError;

    let phoneValidation = Validations.phone(phone);
    const phoneErrors =
      phoneValidation || phone?.length === 0 || !phone
        ? ""
        : "El télefono debe contener 10 dígitos";

    //Set Errors
    setErrors({
      fullName: nameErrors,
      email: emailErrors,
      birthDate: dateError,
      phone: phoneErrors,
    });

    if (nameErrors || emailErrors || dateError || phoneErrors) return;

    dispatcher(
      profileInfo({
        user: {
          id: user.id,
          full_name: name,
          email: email,
          birth_date: birthDate,
          phone: phone,
        },
      })
    );

    setEditableFields(true);
  };

  const EditingInformation = () => {
    setEditableFields(!editableFields);
  };

  const cancel = () => {
    setEditableFields(!editableFields);
    setName(full_name);
    setEmail(userEmail);
    setPhone(phoneNumber);
    setBirthDate(birth_date);
    setErrors({
      fullName: "",
      birthDate: "",
      email: "",
      phone: "",
    });
  };

  if (authReducer.errorMessage === "ERROR")
    return (
      <div className={classes.messageError}>
        <h2>Algo salio mal, intentalo más tarde</h2>
      </div>
    );

  return (
    <Container component="main" maxWidth="sm">
      <ScreenLoader isOpen={authReducer.loading} />
      <form onSubmit={onSubmit} className={classes.form}>
        <div className={classes.headerSection}>
          <Link to="/" className={classes.arrowBack}>
            <ArrowBackIcon />
          </Link>
          <Title value="Mi Perfil" className={classes.title} />
        </div>
        <Avatar alt="Avatar" className={classes.imageLarge}>{(name.charAt(0)).toUpperCase()}</Avatar>
        <TextFieldComponent
          value={name}
          disabled={editableFields}
          error={utilities.returnErrorFlag(errors.fullName)}
          helperText={errors.fullName}
          label="Nombre"
          type="text"
          onChange={(e) => {
            setName(e.target.value);
            setErrors({ ...errors, fullName: "" });
          }}
        />
        <DatePickerComponent
          value={birthDate}
          disabled={editableFields}
          error={utilities.returnErrorFlag(errors.birthDate)}
          label="Fecha de nacimiento"
          disableFuture
          helperText={errors.birthDate}
          onChange={(date) => {
            setBirthDate(date);
            setErrors({ ...errors, birthDate: "" });
          }}
        />
        <TextFieldComponent
          value={email}
          disabled={editableFields}
          error={utilities.returnErrorFlag(errors.email)}
          helperText={errors.email}
          label="Correo electrónico"
          type="text"
          onChange={(e) => {
            setEmail(e.target.value.trim());
            setErrors({ ...errors, email: "" });
          }}
        />
        <TextFieldComponent
          value={phone}
          disabled={editableFields}
          error={utilities.returnErrorFlag(errors.phone)}
          helperText={errors.phone}
          label="Teléfono"
          type="number"
          onChange={(e) => {
            setPhone(e.target.value);
            setErrors({ ...errors, phone: "" });
          }}
        />

        <Link
          className="text-normalize-black "
          hidden={editableFields}
          to="/password"
        >
          <span>Cambiar contraseña</span>
        </Link>

        {editableFields && !authReducer.loading ? (
          <div className={classes.editButton}>
            <ButtonComponent
              type="button"
              value="Editar"
              variant="contained"
              onClick={EditingInformation}
              color="secondary"
            />
          </div>
        ) : (
          <div className={classes.buttonsSection}>
            <ButtonComponent
              type="button"
              value="Cancelar"
              variant="outlined"
              onClick={cancel}
              color="secondary"
            />
            <ButtonComponent
              type="submit"
              value="Guardar"
              variant="contained"
              color="secondary"
            />
          </div>
        )}
      </form>
    </Container>
  );
};

export default MyProfile;
